import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RefreshCw, Play, FileBarChart } from "lucide-react";
import { toast } from "sonner";
import { ModuleShell, DataTable, StatusBadge, ExportButtons, Field } from "@/components/partners/common";
import { ApprovalCard } from "@/components/partners/ProfileTabs";
import { Sel } from "@/components/partners/PartnerForm";
import { KIND, titleCase, fmtDateTime, apiError, fetchMeta } from "@/lib/partnerApi";

export function PartnerApprovals({ kind }) {
  const [status, setStatus] = useState("pending");
  const [mine, setMine] = useState(false);
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    api.get("/partner-master/approvals", { params: { kind, status: status || undefined, mine } })
      .then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  }, [kind, status, mine]);
  useEffect(() => { load(); }, [load]);
  return (
    <ModuleShell kind={kind} title="Approvals" tid={`${kind}-approvals`}
      subtitle="Onboarding approvals follow the configured levels; bank-detail changes and credit-limit overrides are single-step approvals.">
      <div className="flex gap-2 items-center flex-wrap">
        {["pending", "approved", "rejected", ""].map((s) => (
          <button key={s || "all"} type="button" onClick={() => setStatus(s)}
            className={`text-xs px-3 py-1.5 rounded-full border ${status === s ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`}>{s ? titleCase(s) : "All"}</button>
        ))}
        <label className="text-xs text-slate-600 flex items-center gap-1.5 ml-3"><input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} data-testid="mine-only" /> Only what I can act on</label>
        <Button variant="ghost" size="sm" onClick={load}><RefreshCw className="w-4 h-4" /></Button>
      </div>
      <div className="space-y-3">
        {rows.length === 0 && <div className="text-sm text-slate-500 bg-white border border-slate-200 rounded-lg p-6 text-center">Nothing here.</div>}
        {rows.map((a) => <ApprovalCard key={a.id} a={a} showParty onDone={load} />)}
      </div>
    </ModuleShell>
  );
}

export function PartnerAlerts({ kind }) {
  const k = KIND[kind];
  const nav = useNavigate();
  const [status, setStatus] = useState("open");
  const [type, setType] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get(`${k.api}/alerts`, { params: { status: status || undefined } }).then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  }, [k.api, status]);
  useEffect(() => { load(); }, [load]);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${k.api}/alerts/run`);
      const s = data[kind] || {};
      toast.success(`Alert check done — ${s.new || 0} new, ${s.open || 0} open, ${s.resolved || 0} auto-resolved`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const resolve = async (a) => {
    const remarks = window.prompt("Resolution note?") ?? null;
    if (remarks === null) return;
    try { await api.post(`${k.api}/alerts/${a.id}/resolve`, { remarks }); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const types = [...new Set(rows.map((r) => r.alert_type))];
  const shown = type ? rows.filter((r) => r.alert_type === type) : rows;
  return (
    <ModuleShell kind={kind} title="Alerts & Notifications" tid={`${kind}-alerts`}
      subtitle="Checked automatically every day (and on demand). New alerts go to the in-app bell, an email digest and — if configured — WhatsApp/SMS."
      actions={<Button size="sm" onClick={run} disabled={busy} data-testid="run-alerts-btn"><Play className="w-4 h-4 mr-1" /> Run check now</Button>}>
      <div className="flex gap-2 items-center flex-wrap">
        {["open", "resolved"].map((s) => (
          <button key={s} type="button" onClick={() => setStatus(s)}
            className={`text-xs px-3 py-1.5 rounded-full border ${status === s ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`}>{titleCase(s)}</button>
        ))}
        <div className="w-56 ml-2"><Sel value={type} onChange={setType} options={types} placeholder="All alert types" /></div>
      </div>
      <DataTable rows={shown} tid="alerts-table" empty="No alerts." onRowClick={(r) => nav(`${k.base}/${r.partner_id}`)} columns={[
        { key: "severity", label: "Severity", render: (r) => <StatusBadge value={r.severity} /> },
        { key: "alert_type", label: "Type" }, { key: "partner_code", label: "Code" }, { key: "partner_name", label: k.label },
        { key: "title", label: "Alert" }, { key: "message", label: "Detail" },
        { key: "created_at", label: "Raised", render: (r) => fmtDateTime(r.created_at) },
        { key: "channels", label: "Sent via", render: (r) => Object.entries(r.channels || {}).map(([c, v]) => `${c}: ${v}`).join(", ") },
        { key: "_a", label: "", render: (r) => (r.status === "open" ? <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); resolve(r); }}>Resolve</Button> : r.resolved_by) },
      ]} />
    </ModuleShell>
  );
}

export function PartnerReports({ kind }) {
  const k = KIND[kind];
  const [meta, setMeta] = useState(null);
  const [list, setList] = useState([]);
  const [key, setKey] = useState("");
  const [f, setF] = useState({});
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(false);
  const [partners, setPartners] = useState([]);
  useEffect(() => {
    api.get(`${k.api}/reports`).then((r) => { setList(r.data); setKey(r.data[0]?.key || ""); }).catch((e) => toast.error(apiError(e)));
    api.get(k.api).then((r) => setPartners(r.data)).catch(() => {});
    fetchMeta(kind).then(setMeta).catch(() => {});
  }, [k.api, kind]);
  const run = useCallback(async () => {
    if (!key) return;
    setLoading(true);
    try { setRep((await api.get(`${k.api}/reports/${key}`, { params: f })).data); } catch (e) { setRep(null); toast.error(apiError(e)); } finally { setLoading(false); }
  }, [k.api, key, f]);
  useEffect(() => { run(); }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const canExport = (meta?.my_actions || []).includes("export");
  return (
    <ModuleShell kind={kind} title={`${k.label} Reports`} tid={`${kind}-reports`}
      actions={rep && canExport ? <ExportButtons kind={kind} title={rep.title} columns={rep.columns} rows={rep.rows}
        subtitle={Object.entries(rep.filters || {}).map(([a, b]) => `${titleCase(a)}: ${b}`).join(", ")} /> : null}>
      <div className="grid lg:grid-cols-[240px_1fr] gap-5">
        <div className="bg-white border border-slate-200 rounded-lg p-2 h-fit">
          {list.map((r) => (
            <button key={r.key} type="button" onClick={() => setKey(r.key)} data-testid={`report-${r.key}`}
              className={`w-full text-left text-sm px-3 py-2 rounded-md flex items-center gap-2 ${key === r.key ? "bg-blue-50 text-blue-700 font-semibold" : "text-slate-700 hover:bg-slate-50"}`}>
              <FileBarChart className="w-4 h-4 shrink-0" /> {r.title}
            </button>
          ))}
        </div>
        <div className="space-y-3 min-w-0">
          <div className="flex items-end gap-3 flex-wrap bg-white border border-slate-200 rounded-lg p-3">
            <Field label="From"><Input type="date" value={f.date_from || ""} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="h-9" /></Field>
            <Field label="To"><Input type="date" value={f.date_to || ""} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="h-9" /></Field>
            <Field label={k.label} className="w-64">
              <Sel value={f.partner_id} onChange={(v) => setF({ ...f, partner_id: v })} placeholder="All" options={partners.map((p) => ({ value: p.id, label: `${p.code} · ${p.name}` }))} />
            </Field>
            <Field label="Status" className="w-40"><Sel value={f.status} onChange={(v) => setF({ ...f, status: v })} placeholder="Any" options={["active", "inactive", "blocked", "under_review"]} /></Field>
            {key.endsWith("target_vs_achievement") && <Field label="Period"><Input type="month" value={f.period || ""} onChange={(e) => setF({ ...f, period: e.target.value })} className="h-9" /></Field>}
            {key.endsWith("document_expiry") && <Field label="Validity" className="w-36"><Sel value={f.doc_state} onChange={(v) => setF({ ...f, doc_state: v })} placeholder="Any" options={["expired", "expiring", "valid"]} /></Field>}
            <Button onClick={run} disabled={loading} data-testid="run-report-btn">{loading ? "Running…" : "Run"}</Button>
          </div>
          {rep && (
            <>
              <div className="flex items-baseline justify-between">
                <div className="text-lg font-bold text-slate-800" style={{ fontFamily: "Manrope" }}>{rep.title}</div>
                <div className="text-xs text-slate-500">{rep.rows.length} rows · {fmtDateTime(rep.generated_at)}</div>
              </div>
              <DataTable columns={rep.columns} rows={rep.rows} tid="report-table" maxHeight="65vh" />
            </>
          )}
        </div>
      </div>
    </ModuleShell>
  );
}
